import { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemIcon, IconButton, Tooltip, Fade } from '@mui/material';
import { Warning, Info, Clear } from '@mui/icons-material';
import { supabase } from '../lib/supabaseClient';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

interface Notification {
  id: string;
  type: 'emergency' | 'general';
  title: string;
  description: string;
  location?: string;
  timestamp: Date;
  priority: number; // 0 = emergency, 1 = general
}

export default function GeneralNotificationsBox() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [cleared, setCleared] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState<string | null>(null);
  const [clearedLoaded, setClearedLoaded] = useState(false);
  const [notificationsFetched, setNotificationsFetched] = useState(false);

  // Load cleared notifications from localStorage
  useEffect(() => {
    if (clearedLoaded) return; // Only load once
    const saved = localStorage.getItem('clearedGeneralNotifications');
    if (saved) {
      try {
        const loaded = new Set<string>(JSON.parse(saved));
        setCleared(loaded);
        console.log('[GeneralNotificationsBox] Loaded cleared IDs from localStorage:', Array.from(loaded));
      } catch {
        setCleared(new Set());
        console.log('[GeneralNotificationsBox] Failed to parse cleared IDs from localStorage');
      }
    }
    setClearedLoaded(true);
  }, [clearedLoaded]);

  // Save cleared notifications to localStorage
  useEffect(() => {
    localStorage.setItem('clearedGeneralNotifications', JSON.stringify(Array.from(cleared)));
  }, [cleared]);

  // Fetch notifications from emergency_alerts (and general_notifications if exists)
  useEffect(() => {
    if (!clearedLoaded) return; // Only fetch after cleared is loaded
    let subscription: any;
    const fetchNotifications = async () => {
      // Emergency alerts
      const { data: emergencyData, error: emergencyError } = await supabase
        .from('emergency_alerts')
        .select('*')
        .order('created_at', { ascending: false });
      let emergency: Notification[] = [];
      if (emergencyData && !emergencyError) {
        emergency = emergencyData
          .filter((a: any) => a.status === 'active')
          .map((a: any) => ({
            id: a.id,
            type: 'emergency',
            title: a.username ? `Emergency by ${a.username}` : 'Emergency Alert',
            description: a.notes || '',
            location: a.address || '',
            timestamp: new Date(a.created_at),
            priority: 0,
          }));
        console.log('[GeneralNotificationsBox] Fetched notification IDs:', emergency.map(e => e.id));
      }
      // Event status changes from event_schedules
      const { data: eventData, error: eventError } = await supabase
        .from('event_schedules')
        .select('*');
      let eventNotifs: Notification[] = [];
      if (eventData && !eventError) {
        console.log('[GeneralNotificationsBox] Raw event_schedules data:', eventData);
        // Only show recent status changes (e.g., last 24h)
        const now = Date.now();
        const filteredEvents = eventData.filter((e: any) => e.status && e.last_updated && now - new Date(e.last_updated).getTime() < 1000 * 60 * 60 * 24);
        console.log('[GeneralNotificationsBox] Filtered recent status changes:', filteredEvents);
        eventNotifs = filteredEvents.map((e: any) => ({
          id: `event-${e.id}-${e.status}-${e.last_updated}`,
          type: 'general',
          title: e.title ? `Status changed: ${e.title}` : 'Event Status Changed',
          description: `Status is now: ${e.status}`,
          location: e.location || '',
          timestamp: new Date(e.last_updated),
          priority: 1,
        }));
        console.log('[GeneralNotificationsBox] Event notifications:', eventNotifs);
      }
      // Merge and sort
      const allNotifs = [...emergency, ...eventNotifs].sort((a, b) => a.priority - b.priority || b.timestamp.getTime() - a.timestamp.getTime());
      setNotifications(allNotifs);
      setNotificationsFetched(true);
    };
    fetchNotifications();
    subscription = supabase
      .channel('emergency_alerts_realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'emergency_alerts' },
        fetchNotifications
      )
      .subscribe();
    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [clearedLoaded]);

  const handleClear = (id: string) => {
    setCleared(prev => {
      const updated = new Set([...prev, id]);
      // Immediately update localStorage
      localStorage.setItem('clearedGeneralNotifications', JSON.stringify(Array.from(updated)));
      return updated;
    });
    setRemoving(id);
    setTimeout(() => {
      setRemoving(null);
    }, 250); // match fade duration
  };

  const visibleNotifications = notifications.filter(n => !cleared.has(n.id));

  // Debug log before rendering
  if (clearedLoaded && notificationsFetched) {
    console.log('[GeneralNotificationsBox] Final render debug:');
    console.log('Cleared set:', Array.from(cleared));
    console.log('Fetched notification IDs:', notifications.map(n => n.id));
    notifications.forEach(n => {
      console.log(`Notification ID: ${n.id}, cleared.has:`, cleared.has(n.id));
    });
    console.log('Visible notification IDs:', visibleNotifications.map(n => n.id));
  }

  return (
    <Box sx={{
      height: 400,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper',
      borderRadius: 3,
      boxShadow: 3,
      p: 0,
      overflow: 'hidden',
    }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, p: 2, pb: 0 }}>
        <Info color="primary" /> General Notifications
      </Typography>
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        pr: 1,
        mb: 2,
        px: 2,
        pt: 1,
        pb: 2,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#e0e0e0',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
      }}>
        {(!clearedLoaded || !notificationsFetched) ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            Loading notifications...
          </Typography>
        ) : !notifications.length ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No notifications at this time.
          </Typography>
        ) : !visibleNotifications.length ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>
            No notifications at this time.
          </Typography>
        ) : (
          <List disablePadding>
            {visibleNotifications.map((n) => (
              <Fade in={removing !== n.id} timeout={250} key={n.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    mb: 2,
                    bgcolor: n.type === 'emergency' ? 'rgba(255, 0, 0, 0.07)' : 'background.paper',
                    borderRadius: 2,
                    boxShadow: 1,
                    transition: 'box-shadow 0.2s, background 0.2s',
                    '&:hover': {
                      boxShadow: 4,
                      bgcolor: n.type === 'emergency' ? 'rgba(255, 0, 0, 0.13)' : 'grey.100',
                    },
                    position: 'relative',
                  }}
                  secondaryAction={
                    <Tooltip title="Clear this notification">
                      <IconButton
                        onClick={() => handleClear(n.id)}
                        size="small"
                        sx={{
                          color: n.type === 'emergency' ? 'error.main' : 'grey.600',
                          transition: 'color 0.2s',
                          '&:hover': {
                            color: n.type === 'emergency' ? 'error.dark' : 'primary.main',
                          },
                        }}
                      >
                        <Clear />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemIcon sx={{ mt: 1, minWidth: 36 }}>
                    {n.type === 'emergency' ? <Warning color="error" /> : <Info color="primary" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{n.title}</Typography>}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                          {n.description}
                        </Typography>
                        {n.location && (
                          <Typography component="span" variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            <b>Location:</b> {n.location}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" sx={{ color: 'grey.500', fontStyle: 'italic' }}>
                          {dayjs(n.timestamp).fromNow()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              </Fade>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
} 